// Image validation utilities

const VALID_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const VALID_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
const MAX_IMAGES = 15;

/**
 * Validates a single image file
 * @param {File} file - The file to validate
 * @returns {Object} - { valid: boolean, error: string | null }
 */
export const validateImageFile = (file) => {
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  // Check file type
  if (!VALID_IMAGE_TYPES.includes(file.type)) {
    // Fallback to extension check if MIME type is not set
    const fileName = file.name.toLowerCase();
    const hasValidExtension = VALID_EXTENSIONS.some((ext) =>
      fileName.endsWith(ext)
    );

    if (!hasValidExtension) {
      return {
        valid: false,
        error: `Invalid file type. Please upload JPEG, PNG, or WebP images.`,
      };
    }
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File size (${sizeMB}MB) exceeds 5MB limit. Will be compressed.`,
      needsCompression: true,
    };
  }

  return { valid: true, error: null };
};

/**
 * Validates multiple image files
 * @param {FileList | File[]} files - The files to validate
 * @param {number} currentImageCount - Number of images already added
 * @returns {Object} - { valid: boolean, errors: string[], validFiles: File[], invalidFiles: File[] }
 */
export const validateImageFiles = (files, currentImageCount = 0) => {
  const fileArray = Array.from(files);
  const results = {
    valid: true,
    errors: [],
    validFiles: [],
    invalidFiles: [],
    filesNeedingCompression: [],
  };

  // Check total image count
  const totalCount = currentImageCount + fileArray.length;
  if (totalCount > MAX_IMAGES) {
    const allowedCount = MAX_IMAGES - currentImageCount;
    results.errors.push(
      `Cannot add ${fileArray.length} images. You can only add ${allowedCount} more image(s) (max ${MAX_IMAGES} total).`
    );
    results.valid = false;

    // Still process the files we can accept
    fileArray.splice(allowedCount);
  }

  // Validate each file
  fileArray.forEach((file, index) => {
    const validation = validateImageFile(file);

    if (validation.valid || validation.needsCompression) {
      results.validFiles.push(file);
      if (validation.needsCompression) {
        results.filesNeedingCompression.push(file);
      }
    } else {
      results.invalidFiles.push(file);
      if (
        validation.error &&
        !validation.error.includes('Will be compressed')
      ) {
        results.errors.push(`${file.name}: ${validation.error}`);
        results.valid = false;
      }
    }
  });

  return results;
};

/**
 * Gets a human-readable file size
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Export constants for use in components
export const IMAGE_VALIDATION_CONSTANTS = {
  VALID_IMAGE_TYPES,
  VALID_EXTENSIONS,
  MAX_FILE_SIZE,
  MAX_IMAGES,
  ACCEPT_STRING: 'image/jpeg,image/png,image/webp',
};
